import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  delay,
  filter,
  finalize,
  Observable,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { Apis } from '../core/apis';
import { Router } from '@angular/router';
import { KeywordsAndConstants } from '../core/keywords-and-constants';
import { environment } from '../environments/environment';
import { ErrorAction } from '../enums/error-action';
import { ModalButtonType } from '../enums/modal_button_type';
import { MessageType } from '../enums/message-type';
import { GenericError } from '../models/generic-error';
import { Location } from '@angular/common';
import { LocalStorageService } from './services/local-storage.service';
import { v4 as uuidv4 } from 'uuid';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AdminLoginService } from './services/admin/admin-login.service';
import { ModalArguments } from '../models/modal-arguments';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { DialogUtil } from '../utils/dialog-utils';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { UnderMaintenanceModalComponent } from '../app/pages/under-maintenance-modal/under-maintenance-modal.component';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  // modalRef?: BsModalRef;
  private refreshTokenRequestCount: number = 0;
  private tokenBlockRequestCount: number = 0;
  private logoutDurToFailedRefreshTokenRequestThreshold: number = 3;
  private errorDialogCount: number = 0;
  private dialogCount: number = 0;
  private currentUrlsOnProgress: number = 0;
  private gettingReportFormatResponse: boolean = false;
  private id = uuidv4();

  constructor(
    private http: HttpClient,
    public _service: LocalStorageService,
    private dialog: DialogUtil,
    public constants: KeywordsAndConstants,
    private modalServiceForMaintenanceModal: BsModalService,
    private _loginService: AdminLoginService,
    public router: Router,
    private location: Location,
    private loader: SpinnerVisibilityService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = this._service.getToken();
    const isRefreshing = localStorage.getItem('refreshOnProgress');
    if (!request.url.toString().includes('refreshToken')) {
      if (token && this._service.isTokenExpired(token)) {
        if (
          !this._service.refreshing &&
          (isRefreshing == null || isRefreshing == 'false')
        ) {
          return this._service.refreshToken().pipe(
            switchMap((newToken: string) => {
              const cloned = this.addHeaders(request, newToken);
              return this.handleRequest(next, cloned);
            })
          );
        } else {
          return this._service.refreshSubject.pipe(
            filter((token): token is string => token != null),
            take(1),
            switchMap((newToken: string) => {
              const cloned = this.addHeaders(request, newToken);
              return this.handleRequest(next, cloned);
            })
          );
        }
      }
    }

    const cloned = this.addHeaders(request, token);
    return this.handleRequest(next, cloned);
  }

  private handleRequest(next: HttpHandler, request: HttpRequest<any>) {
    this.currentUrlsOnProgress++;
    if (
      request.url.includes('getReportFormatResponse') ||
      (request.url.includes('byCategory') && this.gettingReportFormatResponse)
    ) {
      this.loader.hide();
      this.gettingReportFormatResponse = true;
    } else {
      if (this.gettingReportFormatResponse) {
        this.loader.show();
      }
    }
    return next.handle(request).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        let genericError: GenericError = new GenericError();
        if (error.error instanceof ErrorEvent) {
          throw new GenericError(0, error.error.message);
        } else {
          genericError.message = error.error.message;
          genericError.code = error.error.code;

          // handle 502 and 503 error
          if (
            error.error.status == HttpStatusCode.BadGateway ||
            error.error.status == HttpStatusCode.ServiceUnavailable
          ) {
            genericError.errorAction = ErrorAction.HANDLED;
            this.showMaintainenceModal('Service Unavailable', 0);
          }

          if (
            error.error.message != undefined &&
            error.status == HttpStatusCode.BadRequest
          ) {
            if (error.error.code == 400001) {
              genericError.errorAction = ErrorAction.HANDLED;
              // this.modalRef = this.modalService.show(error.error.message);
            }
          } else {
            if (error.status == HttpStatusCode.Unauthorized) {
              if (error.error.code == 401001) {
                if (request.url.includes('refreshToken')) {
                  this._service.logout();
                } else {
                  this.tokenBlockRequestCount++;
                  if (this.tokenBlockRequestCount == 1) {
                    if (this.refreshTokenRequestCount <= 1) {
                      return this.refreshToken(next, request);
                    }
                  }
                }
              } else if (
                error.error.code == 401002 ||
                error.error.code == 401003
              ) {
                this.tokenBlockRequestCount++;
                if (this.tokenBlockRequestCount == 1) {
                  if (this.refreshTokenRequestCount <= 1) {
                    return this.refreshToken(next, request);
                  }
                }
              } else if (error.error.code == 401004) {
                this._service.logout();
              } else {
              }
            } else if (error.status == HttpStatusCode.Forbidden) {
              if (error.error.code == 403001) {
                genericError.errorAction = ErrorAction.HANDLED;
                this.router.navigateByUrl('/twoFactor', {
                  state: { Access: true },
                });
              } else if (error.error.code == 403007) {
                genericError.errorAction = ErrorAction.HANDLED;
                const contextAlias = this;
                this.dialogCount++;
                if (this.dialogCount == 1) {
                  this.dialog.errorShowModal(
                    new ModalArguments(error.error.message)
                      .withButtonType(ModalButtonType.DOUBLE_BUTTON)
                      .withButtonPositiveText('Added to trusted devices')
                      .withButtonNegativeText('Resend')
                      .dontDismissNegativeButtonOnClick()
                      .withCancellable(false)
                      .withCallback({
                        buttonNegativeClicked() {
                          contextAlias.http
                            .get(Apis.sendMail)
                            .subscribe((res: any) => {
                              contextAlias.dialog.showToast(
                                res.message,
                                MessageType.SUCCESS
                              );
                              setTimeout(
                                () => (contextAlias.dialogCount = 0),
                                300
                              );
                              contextAlias.location.back();
                            });
                        },
                        buttonPositiveClicked() {
                          contextAlias.http
                            .get(Apis.refreshToken)
                            .subscribe((res: any) => {
                              if (res) {
                                contextAlias._service.backUpToken =
                                  JSON.stringify(res.token);
                                contextAlias._service.backUpRefreshToken =
                                  JSON.stringify(res.refreshToken);
                                localStorage.setItem(
                                  contextAlias.constants.LocalStorageToken,
                                  JSON.stringify(res.token)
                                );
                                localStorage.setItem(
                                  contextAlias.constants
                                    .LocalStorageRefreshToken,
                                  JSON.stringify(res.refreshToken)
                                );
                                contextAlias.dialogCount = 0;
                                contextAlias.location.back();
                              }
                            });
                        },
                        buttonSingleClicked() {},
                      })
                  );
                }
              } else if (error.error.code == 403008) {
                genericError.errorAction = ErrorAction.HANDLED;
                if (
                  request.url.includes(
                    'impersonation/checkIfOTPRequiredForImpersonation'
                  )
                ) {
                  return throwError(() => error.error);
                } else {
                  const contextAlias = this;
                  this.dialog.errorShowModal(
                    new ModalArguments(error.error.message)
                      .withButtonType(ModalButtonType.SINGLE_BUTTON)
                      .withButtonSingleText('Ok')
                      .withCallback({
                        buttonNegativeClicked() {},
                        buttonPositiveClicked() {},
                        buttonSingleClicked() {
                          contextAlias.errorDialogCount = 0;
                          contextAlias.location.back();
                        },
                      })
                  );
                }
              } else if (error.error.code == 403009) {
              } else if (error.error.code == 403010) {
                genericError.errorAction = ErrorAction.HANDLED;
                const contextAlias = this;
                this.dialog.errorShowModal(
                  new ModalArguments(error.error.message)
                    .withButtonType(ModalButtonType.SINGLE_BUTTON)
                    .withButtonSingleText('Ok')
                    .withCallback({
                      buttonNegativeClicked() {},
                      buttonPositiveClicked() {},
                      buttonSingleClicked() {
                        contextAlias.router.navigateByUrl(
                          '/password/change-password',
                          {
                            replaceUrl: true,
                          }
                        );
                      },
                    })
                    .withCancellable(false)
                );
              } else if (error.error.code == 403011) {
                genericError.errorAction = ErrorAction.HANDLED;
                const contextAlias = this;
                this.dialog.errorShowModal(
                  new ModalArguments(error.error.message)
                    .withButtonType(ModalButtonType.SINGLE_BUTTON)
                    .withButtonSingleText('Ok')
                    .withCallback({
                      buttonNegativeClicked() {},
                      buttonPositiveClicked() {},
                      buttonSingleClicked() {
                        contextAlias.router.navigateByUrl('/verification', {
                          replaceUrl: true,
                        });
                      },
                    })
                );
              } else if (error.error.code == 403012) {
                genericError.errorAction = ErrorAction.HANDLED;
                this.router.navigateByUrl('/twoFactor');
              }
            } else if (
              error.statusText == 'Request Entity Too Large' ||
              error.status == 413
            ) {
              genericError.errorAction = ErrorAction.HANDLED;
              this.dialog.showToast(error.error.message, MessageType.ERROR);
            } else if (error.status == HttpStatusCode.ServiceUnavailable) {
              genericError.errorAction = ErrorAction.HANDLED;
              let errorCode = error.error.code;
              let message = error.error.message;
              this.showMaintainenceModal(message, errorCode);
              // const initialState = {
              //   errorCode: errorCode,
              //   message: message,
              // };
              // const modalOptions: ModalOptions = {
              //   ignoreBackdropClick: true,
              //   keyboard: false,
              // };
              // this.modalServiceForMaintenanceModal.show(
              //   UnderMaintenanceModalComponent,
              //   { initialState, ...modalOptions }
              // );
              localStorage.setItem(
                this.constants.isModalOpen,
                JSON.stringify(true)
              );
            } else {
              genericError.errorAction = ErrorAction.HANDLED;
              let message = 'Service Unavailable';
              this.showMaintainenceModal(message, 0);
              // const initialState = {
              //   errorCode: 0,
              //   message: message,
              // };
              // const modalOptions: ModalOptions = {
              //   ignoreBackdropClick: true,
              //   keyboard: false,
              // };
              // this.modalServiceForMaintenanceModal.show(
              //   UnderMaintenanceModalComponent,
              //   { initialState, ...modalOptions }
              // );
            }
          }
        }
        return throwError(genericError);
      }),
      tap((response) => {
        if (response instanceof HttpResponse) {
          this.currentUrlsOnProgress--;
          if (this.currentUrlsOnProgress == 0) {
            if (this.gettingReportFormatResponse) {
              this.loader.hide();
              this.gettingReportFormatResponse = false;
            }
          }
        }
      })
    );
  }

  private refreshToken(next: HttpHandler, request: HttpRequest<any>) {
    this.refreshTokenRequestCount++;
    let existingUniqueId = localStorage.getItem('uniqueIdentifierId');
    return this.refreshAccessToken().pipe(
      switchMap((refreshTokenResponse: any) => {
        if (refreshTokenResponse) {
          localStorage.setItem(
            this.constants.LocalStorageToken,
            JSON.stringify(refreshTokenResponse.token)
          );
          localStorage.setItem(
            this.constants.LocalStorageRefreshToken,
            JSON.stringify(refreshTokenResponse.refreshToken)
          );
          this._service.backUpToken = JSON.stringify(
            refreshTokenResponse.token
          );
          this._service.backUpRefreshToken = JSON.stringify(
            refreshTokenResponse.refreshToken
          );
          localStorage.setItem('uniqueIdentifierId', existingUniqueId!!);
          const helper = new JwtHelperService();
          const decodedToken = helper.decodeToken(refreshTokenResponse.token);
          localStorage.setItem(this.constants.isLoginType, decodedToken.type);
          this._loginService.getProfile().subscribe((user: any) => {
            localStorage.setItem(
              this.constants.LocalStorageUserInfo,
              JSON.stringify(user)
            );
          });
          return next.handle(this.addAuthenticationToken(request));
        } else {
          return of();
        }
      }),
      catchError((error: any) => {
        // this._service.logout();
        return of();
      }),
      finalize(() => {
        this.refreshTokenRequestCount = 0;
        this.tokenBlockRequestCount = 0;
      })
    );
  }

  private refreshAccessToken(): Observable<any> {
    return this.http.get(Apis.refreshToken, {
      headers: new HttpHeaders({
        'Access-Control-Allow-Origin': environment.allowed_origin,
        Authorization: `Bearer ${this._service.getRefreshToken()}`,
      }),
    });
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    if (request.url.includes('refreshToken')) {
      return request.clone({
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': environment.allowed_origin,
          Authorization: `Bearer ${this._service.getRefreshToken()}`,
        }),
      });
    } else {
      return request.clone({
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': environment.allowed_origin,
          Authorization: `Bearer ${this._service.getToken()}`,
        }),
      });
    }
  }

  private addHeaders(
    request: HttpRequest<any>,
    token?: string
  ): HttpRequest<any> {
    let headersConfig: any = {
      'Access-Control-Allow-Origin': environment.allowed_origin,
      trackingId: uuidv4(),
      'ngrok-skip-browser-warning': '62490',
    };

    if (!token && !request.url.toString().includes('refreshToken')) {
      return request.clone({ setHeaders: headersConfig });
    }

    if (request.url.toString().includes('refreshToken')) {
      return request.clone({
        setHeaders: {
          ...headersConfig,
          Authorization: `Bearer ${this._service.getRefreshToken()}`,
        },
      });
    }

    if (
      request.url.toString().includes('https://stb-atlpay-bucket') ||
      request.url.toString().includes('https://stb-atlpay-test-bucket') ||
      request.url.toString().includes('https://stb-atlpay-bucket-dev-and-loc')
    ) {
      request = request.clone({
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': 'https://app.atlpay.com',
        }),
      });
    }

    if (request.url.toString().includes('.s3.amazonaws.com')) {
      return request.clone({
        setHeaders: {
          'Access-Control-Allow-Origin': environment.allowed_origin,
        },
      });
    }

    if (request.headers.has('Skip-Loader')) {
      return request.clone({
        setHeaders: {
          ...headersConfig,
          Authorization: `Bearer ${token}`,
          'Skip-Loader': 'true',
        },
      });
    }

    return request.clone({
      setHeaders: {
        ...headersConfig,
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private showMaintainenceModal(message: string, errorCode: number = 0) {
    const initialState = {
      errorCode: errorCode,
      message: message,
    };
    const modalOptions: ModalOptions = {
      ignoreBackdropClick: true,
      keyboard: false,
    };
    this.modalServiceForMaintenanceModal.show(UnderMaintenanceModalComponent, {
      initialState,
      ...modalOptions,
    });
  }
}
